import { createPublicClient, defineChain, http, type Hash, type PublicClient, type TransactionReceipt } from 'viem'

export const ROBINHOOD_RPC = 'https://rpc.mainnet.chain.robinhood.com'
export const ROBINHOOD_EXPLORER = 'https://robinhoodchain.blockscout.com'
export const ROBINHOOD_CHAIN_ID = 4663
export const ROBINHOOD_CHAIN_HEX = `0x${ROBINHOOD_CHAIN_ID.toString(16)}` as const
export const ROBINHOOD_RPC_TIMEOUT_MS = 12_000
export const ROBINHOOD_RECEIPT_TIMEOUT_MS = 90_000
export const ROBINHOOD_RECEIPT_POLL_MS = 2_000

export const robinhoodViemChain = defineChain({
  id: ROBINHOOD_CHAIN_ID,
  name: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: [ROBINHOOD_RPC] },
  },
  blockExplorers: {
    default: {
      name: 'Robinhood Chain Explorer',
      url: ROBINHOOD_EXPLORER,
    },
  },
})

export const robinhoodWalletAddChain = {
  chainId: ROBINHOOD_CHAIN_HEX,
  chainName: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: [ROBINHOOD_RPC],
  blockExplorerUrls: [ROBINHOOD_EXPLORER],
} as const

let cachedClient: PublicClient | undefined

export function getRobinhoodPublicClient(): PublicClient {
  if (!cachedClient) {
    cachedClient = createPublicClient({
      chain: robinhoodViemChain,
      transport: http(ROBINHOOD_RPC, {
        timeout: ROBINHOOD_RPC_TIMEOUT_MS,
        retryCount: 1,
        retryDelay: 400,
      }),
    })
  }
  return cachedClient
}

export function withTimeout<T>(promise: Promise<T>, ms: number, message = 'Request timed out'): Promise<T> {
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled)
        return
      settled = true
      reject(new Error(message))
    }, ms)
    promise.then(
      (value) => {
        if (settled)
          return
        settled = true
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        if (settled)
          return
        settled = true
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function delay(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms))
}

export async function waitForRobinhoodReceipt(
  hash: Hash,
  client: PublicClient = getRobinhoodPublicClient(),
): Promise<TransactionReceipt> {
  const deadline = Date.now() + ROBINHOOD_RECEIPT_TIMEOUT_MS
  while (Date.now() < deadline) {
    const remaining = deadline - Date.now()
    const receipt = await withTimeout(
      client.getTransactionReceipt({ hash }).catch(() => null),
      Math.min(3_000, Math.max(1, remaining)),
      'Request timed out',
    ).catch(() => null)
    if (receipt) {
      if (receipt.status !== 'success')
        throw new Error('Transaction reverted')
      return receipt
    }
    if (Date.now() >= deadline)
      break
    await delay(Math.min(ROBINHOOD_RECEIPT_POLL_MS, Math.max(1, deadline - Date.now())))
  }
  throw new Error('Confirmation is taking too long. Check your wallet, then retry if the transaction did not land.')
}
